import React, { useEffect, useRef, useState } from 'react'
import { Button, Input } from '@yuanfudao/metis-web-component'
import { cleanLoginStorage, saveTokenToStorage } from '@yuanfudao/metis-unified-login'
import { passwordLogin, resetPassword } from '@/service/password-login'
import type { ResetPasswordSession } from '@/service/password-login'
import { validatePassword } from '@/utils/password'
import lessModule from './index.module.less'

const passwordHidden = 'https://miaoduo.fbcontent.cn/private/resource/image/1a1395ad9739146-c4f881bd-18ad-45d7-b7fe-0916ee440be8.svg'

interface InitialPasswordProps {
  session: ResetPasswordSession,
  onCancel: () => void,
  onSuccess: () => boolean | Promise<boolean>,
}

export default function InitialPassword({ session, onCancel, onSuccess }: InitialPasswordProps) {
  const mounted = useRef(true)
  const submitting = useRef(false)
  const consumed = useRef(false)
  const [busy, setBusy] = useState(false)
  const [passwordWasReset, setPasswordWasReset] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [confirmationVisible, setConfirmationVisible] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [confirmationTouched, setConfirmationTouched] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  useEffect(() => {
    if (!session.expireAt) return
    const remaining = session.expireAt - Date.now()
    if (remaining <= 0) {
      setExpired(true)
      return
    }
    const timer = window.setTimeout(() => setExpired(true), Math.min(remaining, 2147483647))
    return () => window.clearTimeout(timer)
  }, [session.expireAt])

  const invalidSession = !session.ticket || !session.phone || !session.loginId || expired
  const mustReturn = passwordWasReset || invalidSession
  const valid = validatePassword(password) && password === confirmation
  const passwordError = passwordTouched && !validatePassword(password)
    ? (password ? '密码格式不正确，请按规则重新设置' : '请输入新密码') : ''
  const confirmationError = confirmationTouched && password !== confirmation
    ? '两次输入的密码不一致，请重新输入' : ''

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (submitting.current) return
    if (mustReturn || consumed.current || (session.expireAt && session.expireAt <= Date.now())) {
      onCancel()
      return
    }
    if (!valid) return
    submitting.current = true
    setBusy(true)
    setSubmitError('')
    let redirecting = false
    try {
      await resetPassword(session, password)
      if (!mounted.current) return
      consumed.current = true
      setPasswordWasReset(true)
      cleanLoginStorage()
      try {
        const result = await passwordLogin(session.loginId, password)
        if (!mounted.current) return
        if (result.needResetPassword || !result.accessToken || !result.refreshToken) {
          throw new Error('请使用新密码重新登录')
        }
        saveTokenToStorage(result.accessToken, result.refreshToken)
      } catch {
        if (mounted.current) setSubmitError('密码已设置成功，请返回登录并使用新密码登录')
        return
      }
      redirecting = await onSuccess()
    } catch (error) {
      if (mounted.current) setSubmitError(error instanceof Error ? error.message : '密码设置失败，请稍后重试')
    } finally {
      // 跳转已发起时持续禁用表单，直到页面卸载。
      if (!redirecting) {
        submitting.current = false
        if (mounted.current) setBusy(false)
      }
    }
  }

  const visibility = (visible: boolean, toggle: () => void) => (
    <Button
      type="text"
      htmlType="button"
      className={lessModule.visibility}
      aria-label={visible ? '隐藏密码' : '显示密码'}
      aria-pressed={visible}
      onMouseDown={(event) => event.preventDefault()}
      onClick={toggle}
    >
      <img src={passwordHidden} alt="" />
    </Button>
  )

  return (
    <main className={lessModule.main}>
      <form className={lessModule.card} onSubmit={handleSubmit} aria-labelledby="initial-password-title">
        <h1 id="initial-password-title">设置新密码</h1>
        <p id="initial-password-rules" className={lessModule.description}>
          {invalidSession ? '改密凭证已失效，请返回登录后重新设置' : '首次使用账号密码登录，请设置新密码。新密码为8-20位，数字、字母、符号的两种或以上组合'}
        </p>
        <Input
          className={lessModule.passwordInput}
          aria-label="新密码"
          aria-describedby="initial-password-rules initial-password-error"
          aria-invalid={Boolean(passwordError)}
          autoComplete="new-password"
          placeholder="请输入新密码"
          type={passwordVisible ? 'text' : 'password'}
          value={password}
          disabled={busy || mustReturn}
          onChange={(event) => { setPassword(event.target.value); setSubmitError('') }}
          onBlur={() => setPasswordTouched(true)}
          suffix={visibility(passwordVisible, () => setPasswordVisible((value) => !value))}
        />
        <Input
          className={lessModule.passwordInput}
          aria-label="确认新密码"
          aria-describedby="initial-password-error"
          aria-invalid={Boolean(confirmationError)}
          autoComplete="new-password"
          placeholder="再次输入新密码"
          type={confirmationVisible ? 'text' : 'password'}
          value={confirmation}
          disabled={busy || mustReturn}
          onChange={(event) => { setConfirmation(event.target.value); setSubmitError('') }}
          onBlur={() => setConfirmationTouched(true)}
          suffix={visibility(confirmationVisible, () => setConfirmationVisible((value) => !value))}
        />
        <div className={lessModule.error} id="initial-password-error" aria-live="polite">
          {submitError || passwordError || confirmationError}
        </div>
        <div className={lessModule.actions}>
          <Button type="bordered" htmlType="button" disabled={busy} onClick={onCancel} className={lessModule.cancel}>取消</Button>
          <Button type="primary" htmlType="submit" disabled={busy || (!mustReturn && !valid)} className={lessModule.submit}>
            {busy ? '设置中...' : mustReturn ? '返回登录' : '设置密码并登录'}
          </Button>
        </div>
      </form>
    </main>
  )
}
